import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import type { SideNavCollapseContextType } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';

export const SideNavCollapseContext = createContext<SideNavCollapseContextType | null>(null);

export const useSideNavCollapse = (): SideNavCollapseContextType => {
  const ctx = useContext(SideNavCollapseContext);
  if (!ctx) {
    throw new Error('useSideNavCollapse must be used inside SideNavCollapseProvider');
  }
  return ctx;
};

/**
 *
 * The SideNavCollapseProvider component is used to provide the collapse state
 * which is used to determine if the sidebar is collapsed or expanded.
 *
 * @param children - The children to render in the provider
 * @returns The SideNavCollapseProvider component
 */
export const SideNavCollapseProvider = ({ children }: PropsWithChildren) => {
  const isMediumSizedScreen = useMediaQuery(breakpoints.down('md'));

  // Not passing the isMediumSizedScreen to useState because
  // isMediumSizedScreen will always be false on the initial render.
  const [isSideNavCollapsed, setIsSideNavCollapsed] = useState<boolean>(false);

  useEffect(() => {
    setIsSideNavCollapsed(isMediumSizedScreen);
  }, [isMediumSizedScreen]);

  const toggle = useCallback<() => void>(() => {
    setIsSideNavCollapsed((prev) => !prev);
  }, []);

  const expand = useCallback<() => void>(() => {
    setIsSideNavCollapsed(false);
  }, []);

  const value: SideNavCollapseContextType = useMemo(
    () => ({
      isSideNavCollapsed,
      toggle,
      expand,
    }),
    [isSideNavCollapsed, toggle, expand]
  );

  return <SideNavCollapseContext.Provider value={value}>{children}</SideNavCollapseContext.Provider>;
};
